import { BookMarked, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { IMPLEMENTATION_GUIDE } from "@/constants/testIds";

/**
 * Recurso de referencia ESTÁTICO (no generable, no firmable). Texto literal extraído de
 * 00_Guia_Implementacion.docx (serie _Riesgo_Bajo) con python-docx, sin intervención de IA.
 */
const SEMAFORO_ROWS = [
  ["Reglamento Interior", "Después de completar y depositar", "Comisión Mixta, firma, depósito, entrega y publicación"],
  ["Vacaciones", "Después de completar plazos y responsables", "Calendario, alertas y autorizaciones trazables"],
  ["Aviso de Privacidad", "Después de completar identidad, canales y transferencias", "Acuse y consentimiento sensible separado cuando aplique"],
  ["Protocolo", "Después de aprobar con trabajadores", "Canal independiente, comité capacitado y formatos"],
  ["Contrato indeterminado", "Después de completar todos los campos y anexos aplicables", "Firma de ambas partes y alta IMSS"],
  ["Contrato determinado", "Sólo para una causa temporal real y probada", "Anexo de temporalidad y evidencia"],
  ["Contrato por obra", "Sólo para obra autónoma y objetivamente terminable", "Anexo técnico y acta de terminación"],
  ["Comisiones y bonos", "Después de firmar anexo económico individual", "Fórmula previa, reporte y no descuentos unilaterales"],
  ["Teletrabajo", "Después de integrar convenio, RIT y NOM-037", "Lista, comisión, equipo, apoyo y capacitación"],
];

export function ImplementationGuideDialog() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          data-testid={IMPLEMENTATION_GUIDE.openButton}
          variant="outline"
          size="sm"
          className="rounded-full border-petroleum/30 text-petroleum gap-2"
        >
          <BookMarked className="w-4 h-4" strokeWidth={1.5} /> Guía de Implementación
        </Button>
      </DialogTrigger>
      <DialogContent data-testid={IMPLEMENTATION_GUIDE.dialog} className="bg-bone max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Guía de Implementación del Paquete de Compliance Laboral</DialogTitle>
          <DialogDescription>Recurso de referencia fijo. No es un documento generable ni firmable.</DialogDescription>
        </DialogHeader>

        <Button
          data-testid={IMPLEMENTATION_GUIDE.downloadPdfButton}
          size="sm" variant="outline"
          onClick={async () => {
            const { data } = await api.get("/document-templates/guia-implementacion/pdf", { responseType: "blob" });
            window.open(URL.createObjectURL(data), "_blank");
          }}
          className="w-fit rounded-full border-petroleum/30 text-petroleum gap-2"
        >
          <Download className="w-4 h-4" /> Descargar PDF
        </Button>

        <div className="space-y-5 text-sm text-charcoal/80">
          <div>
            <p className="font-semibold text-charcoal mb-1">Alcance</p>
            <p className="leading-relaxed">
              Los nueve documentos están redactados para reducir riesgos jurídicos comunes. No existe riesgo cero:
              la protección depende de completar campos, adaptar anexos, aplicar los procedimientos, conservar
              evidencia y actualizar los documentos.
            </p>
          </div>

          <div>
            <p className="font-semibold text-charcoal mb-2">Semáforo de implementación</p>
            <div className="overflow-x-auto rounded-xl border border-petroleum/10">
              <table data-testid={IMPLEMENTATION_GUIDE.semaforoTable} className="w-full text-xs">
                <thead className="bg-petroleum/5">
                  <tr>
                    <th className="text-left p-2 font-semibold text-charcoal">Documento</th>
                    <th className="text-left p-2 font-semibold text-charcoal">Condición para quedar en verde</th>
                    <th className="text-left p-2 font-semibold text-charcoal">Acción indispensable</th>
                  </tr>
                </thead>
                <tbody>
                  {SEMAFORO_ROWS.map((row, i) => (
                    <tr key={row[0]} className={i % 2 === 0 ? "bg-white" : "bg-petroleum/[0.02]"}>
                      {row.map((cell, j) => (
                        <td key={j} className="p-2 align-top text-charcoal/70 border-t border-petroleum/5">{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <p className="font-semibold text-charcoal mb-1">Firma electrónica y acuses</p>
            <ul className="list-disc pl-5 space-y-1 leading-relaxed">
              <li>Políticas, aviso y protocolo: botón de acuse con usuario único, versión, fecha, hora, documento íntegro y registro de auditoría.</li>
              <li>Contratos, anexos económicos y convenios de teletrabajo: firma física o firma electrónica con autenticación, integridad, sello de tiempo y copia descargable.</li>
              <li>RIT: además del acuse, requiere Comisión Mixta, depósito, entrega y publicación.</li>
            </ul>
          </div>

          <div>
            <p className="font-semibold text-charcoal mb-1">Expediente mínimo de evidencia</p>
            <ul className="list-disc pl-5 space-y-1 leading-relaxed">
              <li>Versión final de cada documento.</li>
              <li>Registro de entrega y aceptación.</li>
              <li>Anexos completos.</li>
              <li>Capacitaciones.</li>
              <li>Autorizaciones y decisiones.</li>
              <li>Historial de versiones.</li>
              <li>Evidencias de aplicación consistente.</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
